import React, { ReactElement, useCallback, useContext } from 'react';
import ContextMenuItem, { IContextMenuItemProps } from '../../../components/ContextMenuItem';
import ReturnOrderContextMenuItem from './ReturnOrderContextMenuItem';
import ApprovePriceContextMenuItem from './ApprovePriceContextMenuItem';
import getClientMutationId from '../../../utils/getClientMutationId';
import { useDebouncedTemporarySave } from '../../../components/connectedForm/utils/handleTemporarySave';
import getIn from '../../../components/connectedForm/utils/getIn';
import { useCheckPopUpBlockerFunction } from '../../../utils/CheckPopUpBlockerFunction';
import { appActions } from '../../../state/modules/app';
import {
  AlertType,
  AssignmentType,
  ISalesOrder,
  ISalesOrderLine,
  ISalesOrderLineAssignment,
  ISalesOrderLineInput,
  LineActionRequired,
  Maybe,
  OrderType,
  SalesOrderLineStatus,
} from '../../../graphql/schema/p4types';
import ConnectedFormContext from '../../../components/Context/ConnectedFormContext';
import { useDispatch } from 'react-redux';
import { PROCESSED_SALES_ORDER_LINE_STATUSES } from '../../../utils/LineUtils';
import { useP4ToastController } from '../../system/NotificationsArea';
import { checkPermission } from '../../../utils/OperationPermissions';
import AppContext from '../../../components/Context/AppContext';
import SidebarContext, { SidebarType } from '../../../components/Context/SidebarContext';

const useSalesOrderLineContextActions = ({
  setAddCommentToRow,
}: {
  setAddCommentToRow: React.Dispatch<React.SetStateAction<Maybe<number>>>;
}) => {
  const { change, values } = useContext(ConnectedFormContext);
  const app = useContext(AppContext);
  const salesOrder = values as ISalesOrder;
  const sidebarContext = useContext(SidebarContext);

  const performDebouncedTemporarySave = useDebouncedTemporarySave(false, 500);
  const checkPopUpBlockerFunction = useCheckPopUpBlockerFunction();
  const { dispatchSimpleToast } = useP4ToastController();

  const dispatch = useDispatch();

  const onDeleteClick: IContextMenuItemProps['onClick'] = useCallback(
    (value, close, rowIdx) => {
      if (close) close();

      const mutationId = getClientMutationId();

      //If there are assigned shortages, present a notification (P4-9314) to indicate that they will be removed when the line is archived.
      const affectedLine = getIn(values, `orderLines.${rowIdx}`);
      const activeShortageAssignments: Array<ISalesOrderLineAssignment> =
        (affectedLine.assignments as Array<ISalesOrderLineAssignment>)?.filter(
          (assignment) =>
            assignment.assigned > 0 && !assignment.archived && assignment.assignmentType === AssignmentType.Shortage
        ) ?? [];
      if (activeShortageAssignments.length > 0) {
        dispatchSimpleToast(
          'Line Deletion',
          `The deleted line (${affectedLine?.description}) has one or more shortages which may be removed on save.`,
          AlertType.Info
        );
      }

      change(`orderLines.${rowIdx}.archived`, true, mutationId);
      change(`orderLines.${rowIdx}.archiveIsNotPersisted`, true, mutationId);
      performDebouncedTemporarySave();
    },
    [change, dispatchSimpleToast, performDebouncedTemporarySave, values]
  );

  const onLineAuditClick: IContextMenuItemProps['onClick'] = useCallback(
    (value, close, rowIdx) => {
      const row = getIn(salesOrder, `orderLines.${rowIdx}`);
      if (row) {
        checkPopUpBlockerFunction(
          `${window.location.origin}/Sales/SalesOrderLineAudit/Form/${salesOrder?.id}/${row.id}`,
          '_blank'
        );
      }
    },
    [checkPopUpBlockerFunction, salesOrder]
  );

  const onLineProductClick: IContextMenuItemProps['onClick'] = useCallback(
    (value, close, rowIdx) => {
      const row = getIn(salesOrder, `orderLines.${rowIdx}`);
      if (row && row.product) {
        if (!sidebarContext.openSidebar) {
          dispatch(appActions.toggleSidebar(false, row.product.id, 'ProductForm'));
        } else {
          sidebarContext.openSidebar(SidebarType.ProductForm, row.product.id);
        }
      }
    },
    [dispatch, salesOrder]
  );

  const onAddLineCommentClick: IContextMenuItemProps['onClick'] = useCallback(
    (value, close, rowIdx) => {
      if (close) close();
      setAddCommentToRow(rowIdx);
    },
    [setAddCommentToRow]
  );

  const openBatchAllocationForRowIndex = useCallback(
    (rowIdx: number) => {
      //Setting LineActionRequired to 'Any' will push the batch allocation modal to display now (This function is used by the context menu only).
      change?.(`orderLines.${rowIdx}.batchAllocationRequired`, LineActionRequired.Any);
    },
    [change]
  );

  const checkLineDeleteAvailable = useCallback((salesOrderLine?: ISalesOrderLine) => {
    //AssignedShortage is allowed because per P4-9314 we support archiving a line with a shortage. (The shortage will be removed).
    //Assigned/AssignedAssembly is allowed because instead of deleting, we will display a tooltip that hints towards removing the assignments.
    const allowedStatuses = PROCESSED_SALES_ORDER_LINE_STATUSES.filter(
      (status) =>
        status !== SalesOrderLineStatus.AssignedShortage &&
        status !== SalesOrderLineStatus.Assigned &&
        status !== SalesOrderLineStatus.AssignedAssembly &&
        status !== SalesOrderLineStatus.InProduction
    );

    const lineIsProcessed =
      salesOrderLine?.salesOrderLineStatus?.some((value) => allowedStatuses.includes(value as SalesOrderLineStatus)) ??
      false;
    return !salesOrderLine?.archived && !lineIsProcessed;
  }, []);

  return useCallback(
    ({ rowData, rowIdx }: { rowData: ISalesOrderLineInput; rowIdx: number }) => {
      const renderedButtons: Array<ReactElement> = [];

      if (!rowData.comment) {
        renderedButtons.push(
          <ContextMenuItem
            key={'actionButtonComment'}
            onClick={onAddLineCommentClick}
            label={'Add Comment'}
            rowIdx={rowIdx}
          />
        );
      }

      if (salesOrder.orderType === OrderType.Order && rowData.product?.batchAllocation) {
        renderedButtons.push(
          <ContextMenuItem
            key={'actionButtonBatchAllocate'}
            onClick={() => openBatchAllocationForRowIndex(rowIdx)}
            label={'Batch Allocation'}
            rowIdx={rowIdx}
          />
        );
      }

      //
      const deleteDisabled = rowData?.salesOrderLineStatus?.some(
        (status) => status === SalesOrderLineStatus.Assigned || status === SalesOrderLineStatus.AssignedAssembly
      );

      if (
        checkPermission({ app }, 'RETURN_ORDERS_UPDATE') &&
        rowData?.salesOrderLineStatus?.some(
          (status) => status === SalesOrderLineStatus.Dispatched || status === SalesOrderLineStatus.Invoiced
        )
      ) {
        renderedButtons.push(<ReturnOrderContextMenuItem key={'actionButtonReturnOrder'} rowIdx={rowIdx} />);
      }

      return [
        ...renderedButtons,
        <ContextMenuItem
          key={'actionButtonDelete'}
          onClick={onDeleteClick}
          label={'Delete'}
          available={checkLineDeleteAvailable(rowData as ISalesOrderLine)}
          disabled={deleteDisabled}
          hint={
            deleteDisabled
              ? 'Purchase assignments must be manually removed before a Sales Order Line may be deleted.'
              : undefined
          }
          rowIdx={rowIdx}
        />,
        <ContextMenuItem key={'actionButtonAudit'} onClick={onLineAuditClick} label={'View Audit'} rowIdx={rowIdx} />,
        <ContextMenuItem
          key={'actionButtonProduct'}
          onClick={onLineProductClick}
          label={'View Product'}
          rowIdx={rowIdx}
        />,
        <ApprovePriceContextMenuItem key={'actionButtonApprovePrice'} rowIdx={rowIdx} />,
      ];
    },
    [
      checkLineDeleteAvailable,
      onAddLineCommentClick,
      onDeleteClick,
      onLineAuditClick,
      onLineProductClick,
      openBatchAllocationForRowIndex,
      salesOrder.orderType,
    ]
  );
};

export default useSalesOrderLineContextActions;
